import { orgAPI } from "@/api/api";
import { TreeDataItem, TreeView } from "@/components/tree-view";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils";
import { IntegrationConnection } from "@cohesive/api-client";
import type {
  MicrosoftTeamsChannelEntity,
  MicrosoftTeamsChatEntity,
  MicrosoftTeamsGroupEntity,
  MicrosoftTeamsUserProfileImageEntity,
} from "@cohesive/api-entities";
import {
  getInitials,
  MeetingCard,
  MeetingCardSkeleton,
  MessageCard,
  MessageCardSkeleton,
} from "@cohesive/ui-components";
import { useQueries } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import {
  AtSign,
  Hash as ChannelIcon,
  MessageCircle as ChatIcon,
  Users as TeamIcon,
  Video,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { BaseSelectorProps } from "../BaseSelector";
import {
  Selector,
  SelectorEmpty,
  SelectorNav,
  SelectorNavButton,
  SelectorResults,
  SelectorSearch,
  SelectorSidebar,
} from "../Selector";
import { useMicrosoftTeamsChannels } from "./useMicrosoftTeamsChannels";
import { useMicrosoftTeamsChats } from "./useMicrosoftTeamsChats";
import { useMicrosoftTeamsChatSearch } from "./useMicrosoftTeamsChatSearch";
import { useMicrosoftTeamsMeetings } from "./useMicrosoftTeamsMeetings";
import { useMicrosoftTeamsMessages } from "./useMicrosoftTeamsMessages";
import { useMicrosoftTeamsProfileImage } from "./useMicrosoftTeamsProfileImage";
import { useMicrosoftTeamsTeams } from "./useMicrosoftTeamsTeams";

// Tree item type for TreeView
interface TreeDatItemCustom extends TreeDataItem {
  raw?:
    | MicrosoftTeamsChatEntity
    | MicrosoftTeamsGroupEntity
    | MicrosoftTeamsChannelEntity;
  isTeam?: boolean;
  isChat?: boolean;
  isChannel?: boolean;
}

function ChatAvatar({
  chat,
  connection,
  className,
  style,
}: {
  chat: MicrosoftTeamsChatEntity | MicrosoftTeamsChannelEntity;
  connection: IntegrationConnection;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { data: src } = useMicrosoftTeamsProfileImage({
    connection,
    userID: chat.data.im_user_id || "",
  });

  if (chat.data.is_mpim) {
    return <TeamIcon className="w-4 h-4" />;
  }

  if (src) {
    const displayName = chat.data.im_user_name || chat.data.id || "Chat";

    return (
      <Avatar className={cn("h-4.5 w-4.5", className)} style={style}>
        <AvatarImage src={src} alt={displayName} />
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
    );
  }

  return <TeamIcon className="w-4 h-4" />;
}

export const MicrosoftTeamsSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [navTab, setNavTab] = useState<"chat" | "meet">("chat");

  const [selectedTreeItem, setSelectedTreeItem] =
    useState<TreeDatItemCustom | null>(null);

  const [messageSearchQuery, setMessageSearchQuery] = useState("");
  const debouncedMessageSearchQuery = useDebounce(messageSearchQuery, 500);

  const handleMessageSearchChange = (value: string) => {
    setMessageSearchQuery(value);
  };

  const [meetingSearchQuery, setMeetingSearchQuery] = useState("");
  const debouncedMeetingSearchQuery = useDebounce(meetingSearchQuery, 500);

  const handleMeetingSearchChange = (value: string) => {
    setMeetingSearchQuery(value);
  };

  // Fetch chats
  const {
    data: chatsData = [],
    isLoading: isChatsLoading,
    isError: isChatsError,
  } = useMicrosoftTeamsChats({ connection });

  // Fetch teams
  const {
    data: teamsData = [],
    isLoading: isTeamsLoading,
    isError: isTeamsError,
  } = useMicrosoftTeamsTeams({ connection });

  // Fetch search results
  const {
    data: searchResponse,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useMicrosoftTeamsChatSearch(
    {
      connection,
      query: debouncedMessageSearchQuery,
      limit: 50,
    },
    {
      enabled: !!debouncedMessageSearchQuery,
    },
  );
  const searchResults = searchResponse?.data ?? [];

  // Fetch Meetings
  const {
    data: meetingsResponse,
    isLoading: isMeetingsLoading,
    isError: isMeetingsError,
  } = useMicrosoftTeamsMeetings({
    connection,
    query: debouncedMeetingSearchQuery,
  });
  const meetingsData = meetingsResponse?.data ?? [];

  const { data: channelsData = [] } = useMicrosoftTeamsChannels({
    connection,
  });

  const channelsByTeam = useMemo(() => {
    const channelsByTeam: Record<string, MicrosoftTeamsChannelEntity[]> = {};
    channelsData.forEach((channel) => {
      channelsByTeam[channel.data.team_id] = [
        ...(channelsByTeam[channel.data.team_id] || []),
        channel,
      ];
    });
    return channelsByTeam;
  }, [channelsData]);

  // Build tree data for TreeView
  const treeData: TreeDatItemCustom[] = useMemo(() => {
    // Chats
    const chats: TreeDatItemCustom[] = (chatsData ?? []).map((chat) => ({
      id: chat.data.id,
      name: chat.data.name || chat.data.id || "Chat",
      icon: <ChatAvatar chat={chat} connection={connection} />,
      raw: chat,
      isChat: true,
      actions: [
        <Button
          aria-label="Select chat"
          variant="ghost"
          size="icon"
          className="!p-2 rounded-full w-5 h-5"
          onClick={(e) => {
            e.stopPropagation();
            return onSelect(chat);
          }}
        >
          <AtSign className="!size-3.5" />
        </Button>,
      ],
    }));

    // Teams (with all channels loaded)
    const teams: TreeDatItemCustom[] = (teamsData ?? []).map((team) => {
      const teamId = team.data.id;
      const channels = channelsByTeam[teamId] ?? [];
      return {
        id: teamId,
        name: team.data.name || teamId,
        icon: <TeamIcon className="w-4 h-4" />,
        raw: team,
        isTeam: true,
        children: channels.map((channel) => ({
          id: channel.data.id,
          name: channel.data.name || channel.data.id,
          icon: <ChannelIcon className="w-4 h-4" />,
          raw: channel,
          isChannel: true,
          actions: [
            <Button
              aria-label="Select channel"
              variant="ghost"
              size="icon"
              className="!p-2 rounded-full w-5 h-5"
              onClick={(e) => {
                e.stopPropagation();
                return onSelect(channel);
              }}
            >
              <AtSign className="!size-3.5" />
            </Button>,
          ],
        })),
        actions: [],
      };
    });

    return [
      {
        id: "chats-root",
        name: "Chats",
        icon: <ChatIcon className="w-4 h-4" />,
        children: chats,
      },
      {
        id: "teams-root",
        name: "Teams",
        icon: <TeamIcon className="w-4 h-4" />,
        children: teams,
      },
    ];
  }, [chatsData, teamsData, channelsByTeam, onSelect]);

  // Compute IDs for message fetching at the top level
  let chatId: string | undefined;
  let teamId: string | undefined;
  let channelId: string | undefined;

  if (selectedTreeItem?.isChat) {
    chatId = selectedTreeItem.raw?.data.id;
  } else if (selectedTreeItem?.isChannel) {
    channelId = selectedTreeItem.raw?.data.id;
    // Find parent team
    const parentTeam = teamsData.find((team) =>
      channelsByTeam[team.data.id]?.some((ch) => ch.data.id === channelId),
    );
    teamId = parentTeam?.data.id;
  }

  // Fetch messages at the top level
  const {
    data: messagesResponse,
    isLoading: isMessagesLoading,
    isError: isMessagesError,
  } = useMicrosoftTeamsMessages({
    connection,
    chatId,
    teamId,
    channelId,
    limit: 20,
  });
  const messages = messagesResponse?.data ?? [];

  const uniqueUserIds = useMemo(() => {
    return [
      ...new Set(
        messages.map((message) => message.data.user_id).filter(Boolean),
      ),
    ] as string[];
  }, [messages]);

  const avatarResults = useQueries({
    queries: uniqueUserIds.map((id) => ({
      queryKey: ["microsoft-teams-profile-image", connection.id, id],
      queryFn: async () => {
        const response = await orgAPI.toolbox.execute<
          MicrosoftTeamsUserProfileImageEntity,
          Record<string, unknown>
        >({
          integration_connection_id: connection.id,
          action_name: "microsoft-teams-get-user-profile-image",
          action_input: {
            user_id: id,
          },
        });

        const src = `data:${response.data.data.content_type};base64,${response.data.data.data}`;

        return { id, src };
      },
    })),
  });

  const meetingIds = useMemo(
    () => meetingsData.map((e) => e.data.id),
    [meetingsData],
  );
  const searchResultIds = useMemo(
    () => searchResults.map((e) => e.data.id),
    [searchResults],
  );
  const messageIds = useMemo(() => messages.map((e) => e.data.id), [messages]);

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed message/meeting rows.
  const meetingsGenericById = useMemo(
    () => buildGenericById(toGenericArray(meetingsResponse?.data_generic)),
    [meetingsResponse],
  );
  const searchGenericById = useMemo(
    () => buildGenericById(toGenericArray(searchResponse?.data_generic)),
    [searchResponse],
  );
  const messagesGenericById = useMemo(
    () => buildGenericById(toGenericArray(messagesResponse?.data_generic)),
    [messagesResponse],
  );

  const userAvatars = useMemo(() => {
    return avatarResults
      .filter((result) => result.data && result.data.id && result.data.src)
      .reduce(
        (acc, result) => {
          if (result.data?.id && result.data?.src) {
            acc[result.data.id] = result.data.src;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [avatarResults]);

  // Render right pane: show prompt or selected chat/channel details (future: threads/messages)
  const renderDetails = () => {
    if (navTab === "meet") {
      if (isMeetingsLoading) {
        return (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <MeetingCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        );
      }

      if (isMeetingsError) {
        return <SelectorEmpty message="Error loading meetings" />;
      }

      if (!meetingsData.length) {
        return <SelectorEmpty message="No meetings found" />;
      }

      return (
        <div className="divide-y">
          {meetingsData.map((entity) => (
            <EntityDragRow
              key={entity.data.id}
              entity={entity}
              generic={meetingsGenericById.get(entity.data.id)}
              entityId={entity.data.id}
              orderedIds={meetingIds}
              onSelect={onSelect}
              dragScope="ms-teams"
            >
              <MeetingCard
                {...entity.data}
                platform={entity.platform}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            </EntityDragRow>
          ))}
        </div>
      );
    }

    if (navTab === "chat") {
      if (isMessagesLoading || isSearchLoading) {
        return (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <MessageCardSkeleton
                key={i}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            ))}
          </div>
        );
      }
      if (messageSearchQuery && searchResults.length && !isSearchLoading) {
        return (
          <div className="divide-y">
            {searchResults.map((entity) => (
              <EntityDragRow
                key={entity.data.id}
                entity={entity}
                generic={searchGenericById.get(entity.data.id)}
                entityId={entity.data.id}
                orderedIds={searchResultIds}
                onSelect={onSelect}
                dragScope="ms-teams"
              >
                <MessageCard
                  {...entity.data}
                  user_image_url={userAvatars[entity.data.user_id || ""]}
                  platform={entity.platform}
                  className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
                />
              </EntityDragRow>
            ))}
          </div>
        );
      }
      if (!selectedTreeItem) {
        return (
          <SelectorEmpty message="Select a chat or channel to view details" />
        );
      }
      if (isMessagesError || isSearchError) {
        return <SelectorEmpty message="Error loading messages" />;
      }
      if (!messages.length) {
        return <SelectorEmpty message="No messages found" />;
      }
      return (
        <div className="divide-y">
          {messages.map((entity) => (
            <EntityDragRow
              key={entity.data.id}
              entity={entity}
              generic={messagesGenericById.get(entity.data.id)}
              entityId={entity.data.id}
              orderedIds={messageIds}
              onSelect={onSelect}
              dragScope="ms-teams"
            >
              <MessageCard
                {...entity.data}
                platform={entity.platform}
                user_image_url={userAvatars[entity.data.user_id || ""]}
                className="shadow-none last:border-none rounded-none border-t-0 border-r-0 border-l-0"
              />
            </EntityDragRow>
          ))}
        </div>
      );
    }

    return null;
  };

  // Define nav bar once
  const nav = (
    <SelectorNav>
      <SelectorNavButton
        selected={navTab === "chat"}
        icon={<ChatIcon />}
        onClick={() => setNavTab("chat")}
        aria-label="Chat"
      >
        Chat
      </SelectorNavButton>
      <SelectorNavButton
        selected={navTab === "meet"}
        icon={<Video />}
        onClick={() => setNavTab("meet")}
        aria-label="Meet"
      >
        Meet
      </SelectorNavButton>
    </SelectorNav>
  );

  return (
    <Selector>
      {nav}
      <SelectorSidebar>
        {navTab === "chat" &&
        (isChatsLoading || isTeamsLoading || isChatsError || isTeamsError) ? (
          <div className="p-2 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : navTab === "chat" ? (
          <TreeView
            data={treeData}
            onSelectChange={(item) => setSelectedTreeItem(item || null)}
            expandAll
            className="h-full overflow-auto"
          />
        ) : null}
      </SelectorSidebar>

      {navTab === "chat" && (
        <SelectorSearch
          value={messageSearchQuery}
          onChange={handleMessageSearchChange}
          placeholder="Search messages..."
        />
      )}

      {navTab === "meet" && (
        <SelectorSearch
          value={meetingSearchQuery}
          onChange={handleMeetingSearchChange}
          placeholder="Search meetings..."
        />
      )}

      <SelectorResults>{renderDetails()}</SelectorResults>
    </Selector>
  );
};
